import React, { ReactNode } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline"; // Import CssBaseline
import './theme.d.ts'; // Import theme type declarations

const theme = createTheme({
  palette: {
    primary: {
      main: "#5e67b6", // vivid indigo-blue
      light: "#8575cb", // soft bright purple
      dark: "#48528d", // royal blue
    },
    secondary: {
      main: "#726ec1", // brighter purple
      light: "#8575cb", // soft bright purple
      dark: "#484463", // muted violet
    },
    error: {
      main: "#d32f2f", // A deep red for error
    },
    background: {
      default: "#31343f", // dark blue-gray
      paper: "#f8f9fa", // off-white for maximum readability
    },
  },
  // Add custom theme properties
  custom: {
    gradients: {
      primary: "linear-gradient(90deg, #5e67b6 0%, #726ec1 100%)",
      button: "linear-gradient(90deg, #5e67b6 0%, #726ec1 100%)",
      buttonHover: "linear-gradient(90deg, #48528d 0%, #484463 100%)",
    },
    typography: {
      weights: {
        bold: 600,
        medium: 600,
      },
    },
    spacing: {
      borderRadius: 2,
    },
    colors: {
      linkColor: "#5e67b6", // vivid indigo-blue for links on light background
      accent: "#3d405a", // deep indigo
      muted: "#39373a", // muted brown-gray
      charcoal: "#373636", // charcoal
      smokyPlum: "#433e44", // smoky plum
      deepBluGray: "#333e5f", // deep blue-gray
      deepMutedBrown: "#312f32", // deep muted brown
      primaryText: "#2d3748", // dark gray for headings on light background
      secondaryText: "#4a5568", // medium gray for body text on light background
      brighterPurple: "#726ec1", // brighter purple
      royalBlue: "#48528d", // royal blue
      deepIndigo: "#3d405a", // deep indigo
      mutedBrownGray: "#39373a", // muted brown-gray (branches)
      darkGrayAccent: "#32343b", // dark gray accent
      softBrightPurple: "#8575cb", // soft bright purple
      vividIndigoBlue: "#5e67b6", // vivid indigo-blue
      mutedViolet: "#484463", // muted violet
      deepBlueGray: "#31343f", // dark blue-gray
      brownGrayBranch: "#39383c", // brown-gray (branch tone)
      mutedEarthyGray: "#323332", // muted earthy gray
      nearBlackGray: "#313133", // near black-gray
    },
  },
  typography: {
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    h1: {
      fontWeight: 600,
    },
    h2: {
      fontWeight: 600,
    },
    h3: {
      fontWeight: 600,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          margin: 0,
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
          backgroundColor: "#31343f", // dark blue-gray
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          textTransform: "none",
          fontWeight: 600,
        },
        containedPrimary: {
          background: "linear-gradient(90deg, #5e67b6 0%, #726ec1 100%)",
          '&:hover': {
            background: "linear-gradient(90deg, #48528d 0%, #484463 100%)",
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          backgroundColor: "#f8f9fa", // off-white for maximum readability
          flex: 1,
          overflow: "auto",
        },
        paper: {
          backgroundColor: "#f8f9fa", // off-white for maximum readability
          borderRight: "1px solid rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: "linear-gradient(90deg, #5e67b6 0%, #726ec1 100%)",
          position: "fixed",
          zIndex: 1201,
          height: 64,
          boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.3)",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 64,
        },
      },
    },
  },
});

// Define prop types for children
interface ThemeWrapperProps {
  children: ReactNode;
}

const ThemeWrapper: React.FC<ThemeWrapperProps> = ({ children }) => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

export default ThemeWrapper;
